import { Component } from '@angular/core';
import { Router } from '@angular/router';
import {
    LoggedInUser,
    NameValue,
    OnboardingService,
    OrganisationService,
    PersonDto,
    PersonsOrganisationDto,
    PreferencesService,
    SimpleOrganisationDto,
    UserService,
    UserStatus
} from '@flyfreely-portal-ui/flyfreely';
import {
    DashboardSelectionOptions,
    findAvailableDefaultDashboardsByOrganisationFeatureFlag
} from '@flyfreely-portal-ui/ui';
import { combineLatest, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

/**
 * This component is used to display a loading spinner while the user is waiting for their profile to load, and then redirect them to the expected organisation, or onboarding.
 *
 * It is expected to only ever show at the root level of the application.
 *
 * See {@link https://decmech.atlassian.net/wiki/spaces/FLYF/pages/2932277254/Signup+Invite+Demo+and+Onboarding+Behaviour}
 */
@Component({
    template: `<ngx-loading [show]="true"></ngx-loading>`
})
export class LandingComponent {
    currentUser: PersonDto;
    currentOrganisations: SimpleOrganisationDto[];

    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        private userService: UserService,
        private router: Router,
        private onboardingService: OnboardingService,
        private preferencesService: PreferencesService,
        private organisationService: OrganisationService
    ) {}

    ngOnInit() {
        combineLatest([
            this.userService.userChange$.pipe(
                filter<LoggedInUser>(c => c.type === UserStatus.LOGGED_IN)
            ),
            this.onboardingService.findDemoOrganisations(),
            this.preferencesService.findPreferences<{
                defaultDashboard?: DashboardSelectionOptions;
            }>('profile-options', null)
        ])
            .pipe(take(1), takeUntil(this.ngUnsubscribe$))
            .subscribe(([userChanges, demoOrganisations, userPreferences]) =>
                this.resolveInitialRedirect(
                    userChanges.currentUser,
                    userChanges.currentUsersOrganisations,
                    demoOrganisations,
                    userPreferences
                )
            );
    }

    /**
     * Determine whether to redirect to the normal user flow, the demo organisation, or onboarding, based
     * on whether the user has completed onboarding or not.
     * @param user the logged in user
     * @param organisations the available organisations
     * @param demoOrganisations the demo organisations
     */
    private resolveInitialRedirect(
        user: PersonDto,
        organisations: SimpleOrganisationDto[],
        demoOrganisations: NameValue<number>[],
        profileSettings?: {
            defaultDashboard?: DashboardSelectionOptions;
        }
    ) {
        const demoOrganisationIdList = demoOrganisations.map(org => org.value);

        const demoOrganisation = organisations.find(org =>
            demoOrganisationIdList.includes(org.id)
        );

        this.currentUser = user;
        this.currentOrganisations = organisations;

        this.onboardingService.findUserOnboardingStatus().then(onboarded => {
            onboarded
                ? this.redirectToDashboard(
                      this.currentUser,
                      profileSettings?.defaultDashboard
                  )
                : demoOrganisation != null &&
                  (user.defaultOrganisationId == null ||
                      user.defaultOrganisationId === demoOrganisation.id)
                ? this.redirectTo(demoOrganisation.id)
                : this.redirectToOnboarding();
        });
    }

    /**
     * Maps a `DashboardSelectionOptions` value (or undefined) to the corresponding route path.
     */
    private dashboardToRoute(dashboard: string | undefined): string {
        switch (dashboard) {
            case DashboardSelectionOptions.MISSIONS:
                return 'missions';
            case DashboardSelectionOptions.REPORTING:
                return 'reporting';
            case DashboardSelectionOptions.TASKS:
                return 'tasks';
            case DashboardSelectionOptions.MANAGE:
            default:
                return 'home';
        }
    }

    /**
     * We only want to go to a default dashboard route if that organisation has it available.
     *
     * @param organisation the current loaded organisation
     * @param defaultDashboard the DashboardSelectionOptions value for either the profile's or the organisation's default dashboard
     * @returns the dashboard option, if it's available in the org
     * If it isn't available the function will return null to allow a fall through option
     */
    private adjustOrganisationDefaultDashboardByFeatureFlag(
        organisation: PersonsOrganisationDto,
        defaultDashboard: string | null
    ) {
        const allowedOptions =
            findAvailableDefaultDashboardsByOrganisationFeatureFlag(
                organisation
            );
        if (
            defaultDashboard == null ||
            allowedOptions.some(option => option === defaultDashboard)
        ) {
            return defaultDashboard;
        } else {
            return null;
        }
    }

    /**
     * Resolves the default dashboard route for the given organisation, consulting user preferences
     * first, then falling back to the organisation's settings, then to 'home'.
     *
     * Priority: user profile-options preference > organisation settings.defaultDashboard > 'home'
     */
    private resolveOrgDefaultDashboard(organisation: PersonsOrganisationDto) {
        if (organisation == null) {
            this.router.navigate(['home']);
            return;
        }
        // Only route to a default dashboard if the organisation has it available.
        const route = this.dashboardToRoute(
            this.adjustOrganisationDefaultDashboardByFeatureFlag(
                organisation,
                organisation.settings.defaultDashboard
            )
        );
        this.redirectTo(organisation.id, route);
    }

    private redirectToDashboard(
        user: PersonDto,
        profileDefaultDashboard: DashboardSelectionOptions | undefined
    ) {
        const orgId = user.defaultOrganisationId ?? user.personalOrganisationId;
        this.organisationService
            .findByIdForUser(orgId, orgId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                const profileDefaultDashboardForOrg =
                    this.adjustOrganisationDefaultDashboardByFeatureFlag(
                        organisation,
                        profileDefaultDashboard
                    );
                if (profileDefaultDashboardForOrg != null) {
                    const route = this.dashboardToRoute(
                        profileDefaultDashboardForOrg
                    );
                    this.redirectTo(orgId, route);
                } else {
                    this.resolveOrgDefaultDashboard(organisation);
                }
            });
    }

    private redirectTo(organisationId: number, path?: string) {
        this.router.navigate([path ?? 'home'], {
            queryParams: { organisationId }
        });
    }

    private redirectToOnboarding() {
        this.router.navigate(['onboarding']);
    }
}
